import React, { useEffect, useMemo, useState } from "react";
import TickIcon from "../../../images/Builder/checkmark.svg";
import NewLabelIcon from "../../../images/Builder/new_badge.svg";
import ExpiryDropdown from "./ExpiryDropdown";
import { useBuilderContext } from "../context/BuilderContext";
import SearchIcon from "../../../images/orders/search_icon.svg";
import { useApplyPreBuiltStrategy } from "../helpers/ApplyPrebuilt";
import DeleteIcon from "../../../images/icons/delete.svg";
import EditIcon from "../../../images/icons/edit.svg";
import { useTheme } from "../../../context/ThemeContext";
import SuccessIcon from "../../../images/success_icon.svg";
import { toast } from "react-toastify";
import CustomToastContent from "../../../components/CustomToastContent";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import SaveStrategyModal from "./SaveStrategyModal";
import { useSavedStrategies } from "../context/SavedStrategiesContext";
import SnackbarToast from "../../../components/SnackbarToast";
import { getCookie } from "../../../utils/cookieUtils";

const PrebuiltStrategies = ({
  strategies,
  activeCategory,
  setActiveCategory,
  selectedStrategy,
  handleStrategySelect,
  handleCallback,
  scrollToTop,
}) => {
  const {
    selectedSymbol,
    selectedExpiry,
    setSelectedExpiry,
    setSymbolsActiveCategory,
  } = useBuilderContext();

  // Use context instead of local state
  const {
    savedStrategies,
    setSavedStrategies,
    loading,
    error,
    fetchSavedStrategies,
  } = useSavedStrategies();

  const { theme } = useTheme();
  const { applySavedStrategy } = useApplyPreBuiltStrategy();

  // const [savedStrategies, setSavedStrategies] = useState([]);
  // const [loading, setLoading] = useState(false);
  // const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [deletingStrategy, setDeletingStrategy] = useState(null);

  // State for confirmation dialog
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    strategy: null,
  });

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState(null);

  // // Fetch saved strategies from API
  // const fetchSavedStrategies = async () => {
  //   try {
  //     setLoading(true);
  //     setError(null);

  //     const client_id = getCookie("client_id");

  //     if (!client_id) {
  //       throw new Error("Client ID not found in localStorage");
  //     }

  //     const response = await fetch(
  //       "https://zp4749ggcf.execute-api.ap-south-1.amazonaws.com/default/getSavedStrategies",
  //       {
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json",
  //         },
  //         body: JSON.stringify({
  //           user_id: client_id,
  //         }),
  //       }
  //     );

  //     if (!response.ok) {
  //       throw new Error(`HTTP error! status: ${response.status}`);
  //     }

  //     const data = await response.json();
  //     setSavedStrategies(data || []);
  //   } catch (err) {
  //     console.error("Error fetching saved strategies:", err);
  //     setError(err.message);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Open delete confirmation dialog
  const handleDeleteClick = (strategy) => {
    setDeleteConfirmation({
      isOpen: true,
      strategy: strategy,
    });
  };

  // Close delete confirmation dialog
  const handleDeleteCancel = () => {
    setDeleteConfirmation({
      isOpen: false,
      strategy: null,
    });
  };

  // Confirm delete action
  const handleDeleteConfirm = async () => {
    const strategy = deleteConfirmation.strategy;
    if (!strategy) return;

    try {
      setDeletingStrategy(strategy.id);

      const client_id = getCookie("client_id");

      if (!client_id) {
        throw new Error("Client ID not found in localStorage");
      }

      const response = await fetch(
        "https://1z72ksymml.execute-api.ap-south-1.amazonaws.com/default/deleteSavedStrategies",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: client_id,
            id: strategy.id,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status) {
        // Remove the deleted strategy from the local state
        setSavedStrategies((prev) => prev.filter((s) => s.id !== strategy.id));

        // Close the confirmation dialog
        setDeleteConfirmation({
          isOpen: false,
          strategy: null,
        });

        toast("Strategy deleted successfully!", {
          position: "bottom-center",
          autoClose: 1000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "dark",
          className: "custom-snackbar",
          bodyClassName: "custom-snackbar-body",
        });
      } else {
        throw new Error(data.message || "Failed to delete strategy");
      }
    } catch (err) {
      console.error("Error deleting saved strategy:", err);

      toast.error(
        <CustomToastContent
          heading="Delete failed"
          message={err.message || "Failed to delete saved strategy"}
        />,
        {
          className: "custom-error-toast",
        }
      );
    } finally {
      setDeletingStrategy(null);
    }
  };

  const handleEditStrategy = (strategy) => {
    setEditingStrategy(strategy);
    setEditModalOpen(true);
  };
  // Filter saved strategies based on search term
  const filteredSavedStrategies = useMemo(() => {
    // Ensure savedStrategies is always an array
    const strategiesArray = Array.isArray(savedStrategies) ? savedStrategies : [];

    if (!searchTerm.trim()) {
      return strategiesArray;
    }

    return strategiesArray.filter((strategy) => {
      const searchLower = searchTerm.toLowerCase();

      // Search in strategy name
      const nameMatch = strategy.name?.toLowerCase().includes(searchLower);

      // Search in created date
      const dateMatch = strategy.created_on
        ?.toLowerCase()
        .includes(searchLower);

      // Search in buy_sell_list info
      const buySeelListMatch = strategy.buy_sell_list?.some((item) =>
        JSON.stringify(item).toLowerCase().includes(searchLower)
      );

      // Search in any other relevant fields
      const avatarMatch = strategy.avatar?.toLowerCase().includes(searchLower);

      return nameMatch || dateMatch || buySeelListMatch || avatarMatch;
    });
  }, [savedStrategies, searchTerm]);

  // Clear search when switching away from saved category
  useEffect(() => {
    if (activeCategory !== "saved") {
      setSearchTerm("");
    }
  }, [activeCategory]);

  // Fetch saved strategies when component mounts or when activeCategory changes to "saved"
  useEffect(() => {
    if (activeCategory === "saved") {
      fetchSavedStrategies();
    }
  }, [activeCategory]);

  // Handle saved strategy selection
  const handleSavedStrategySelect = (strategy) => {
    scrollToTop();
    try {
      // // console.log("apply wali strategy jo saved hai", strategy);
      const childData = applySavedStrategy(strategy);
      if (childData && childData.length > 0) {
        // debugger
        // Call the callback function with the constructed data
        handleCallback([childData], false);
        // // console.log("Applied saved strategy:", strategy.name);
      } else {
        console.warn("No data generated from saved strategy");
      }
    } catch (error) {
      console.error("Error applying saved strategy:", error);
    }
  };
  // // console.log("filteredSavedStrategies", filteredSavedStrategies);

  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setEditingStrategy(null);
  };

  const handleEditModalSave = (updatedName, updatedRows) => {
    // Update the strategy in local state
    setSavedStrategies((prev) =>
      prev?.map((strategy) =>
        strategy.id === editingStrategy.id
          ? {
            ...strategy,
            name: updatedName,
            strategy: {
              buy_sell_list: updatedRows?.map((row) =>
                row.isBuy ? "Buy" : "Sell"
              ),
              call_put_list: updatedRows?.map((row) =>
                row.isFuture ? "Fut" : (row.isCALL ? "Call" : "Put")
              ),
              lots_list: updatedRows?.map((row) => row.lotSize),
              atmindex_list: updatedRows?.map((row) => row.atmIndex),
              expiries: updatedRows?.map((row) => row.expiryIndex),
            },
          }
          : strategy
      )
    );

    // toast("Strategy updated successfully!", {
    //   position: "bottom-center",
    //   autoClose: 1000,
    //   hideProgressBar: true,
    //   closeOnClick: false,
    //   pauseOnHover: true,
    //   draggable: true,
    //   progress: undefined,
    //   theme: "dark",
    //   className: "custom-snackbar",
    //   bodyClassName: "custom-snackbar-body",
    // });
  };

  return (
    <div>
      <div className=" text-[14px] text-p300 font-semibold p-2">
        Pre Built Strategies
      </div>
      <div className=" bg-p400 rounded-md">
        <div className="flex justify-between items-center px-3 py-2 border-b border-n100 border-opacity-50">
          <ExpiryDropdown
            expiry={selectedExpiry}
            expiries={selectedSymbol?.option_expiries}
            onSelect={(selected) => {
              setSelectedExpiry(selected);
            }}
          />
          <div className="flex justify-start px-3 gap-2">
            {Object.keys(strategies)?.map((category) => (
              <button
                key={category}
                className={`cursor-pointer mx-2 py-1 text-[12px] transition duration-150 ease-in-out
  ${activeCategory === category
                    ? " text-p300 font-medium border-b-2 border-p150 py-0"
                    : " text-n500 "
                  }
`}
                onClick={() => {
                  setSymbolsActiveCategory((prev) => ({
                    ...prev,
                    [selectedSymbol?.scrip]: category,
                  }));
                }}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Strategy Grid */}

        {/* Saved Strategies Section */}
        {activeCategory === "saved" ? (
          <div className="grid grid-cols-1 gap-4 gap-y-2 py-3 overflow-y-auto max-h-[310px] no-scrollbar ">
            <div className="flex flex-col gap-2">
              {/* Search Input */}
              <div className="relative  px-3">
                <input
                  type="text"
                  placeholder="Search saved strategy"
                  className="w-full h-10 rounded-[4px] border-none pl-8 pr-3 text-xs font-medium text-n400 focus:outline-none bg-n100"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <div className="absolute left-5 top-1/2 transform -translate-y-1/2">
                  <img src={SearchIcon} alt="search-icon" />
                </div>
                {/* Clear search button */}
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-n500 hover:text-n400 transition duration-150">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>

              {/* Loading State */}
              {loading && (
                <div className="px-3">
                  <div className="flex items-center justify-center p-6 border-2 border-dashed border-n100 rounded-md bg-n100 bg-opacity-20 min-h-[140px]">
                    <div className="text-n500 text-sm font-medium">
                      Loading saved strategies...
                    </div>
                  </div>
                </div>
              )}

              {/* Error State */}
              {error && (
                <div className="px-3">
                  <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-n100 rounded-md bg-n100 bg-opacity-20 min-h-[140px]">
                    <div className="text-red text-sm text-center font-medium">Error: {error}</div>
                    <div className="text-n500 text-xs text-center mt-2 opacity-70">
                      Please try again later
                    </div>
                  </div>
                </div>
              )}

              {/* No Results State */}
              {!loading &&
                !error &&
                searchTerm &&
                filteredSavedStrategies?.length === 0 && (
                  <div className="px-3">
                    <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-n100 rounded-md bg-n100 bg-opacity-20 min-h-[140px]">
                      <div className="text-n500 text-sm text-center font-medium">
                        No strategies found for "{searchTerm}"
                      </div>
                      <div className="text-n500 text-xs text-center mt-2 opacity-70">
                        Try a different search term
                      </div>
                    </div>
                  </div>
                )}

              {/* Empty State */}
              {!loading &&
                !error &&
                !searchTerm &&
                filteredSavedStrategies.length === 0 && (
                  <div className="px-3">
                    <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-n100 rounded-md bg-n100 bg-opacity-20 min-h-[140px]">
                      <div className="text-n500 text-sm text-center font-medium">
                        No saved strategies found
                      </div>
                      <div className="text-n500 text-xs text-center mt-2 opacity-70">
                        Create and save strategies to see them here
                      </div>
                    </div>
                  </div>
                )}

              {/* Saved Strategies List */}
              {!loading &&
                !error &&
                filteredSavedStrategies?.map((strategy) => (
                  <div
                    key={strategy.id}
                    className="flex items-center justify-between px-3 py-2 border-b-2 border-n100  transition duration-150 cursor-pointer"
                    onClick={() => handleSavedStrategySelect(strategy)}>
                    {/* Left side - Avatar and Info */}
                    <div className="flex items-center gap-3">
                      {/* Avatar */}
                      <div className="w-12 h-12 bg-p300 rounded-full flex items-center justify-center">
                        <span className="text-p400 font-semibold text-lg">
                          {strategy.avatar ||
                            strategy.name?.substring(0, 2)?.toUpperCase() ||
                            "SS"}
                        </span>
                      </div>

                      {/* Strategy Info */}
                      <div className="flex flex-col">
                        <h3 className="text-p300 font-medium text-sm">
                          {strategy.name || "Untitled Strategy"}
                        </h3>
                        <div className="flex items-center gap-4 text-n500 text-xs">
                          <span>
                            Created:{" "}
                            {strategy.created_on ||
                              strategy.created_on ||
                              "N/A"}
                          </span>
                          <span>
                            • {strategy?.strategy?.buy_sell_list?.length || 0}{" "}
                            positions
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Right side - Action buttons */}
                    <div className="flex items-center gap-2">
                      {/* Edit Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditStrategy(strategy);
                        }}
                        className="p-1 hover:bg-n100 rounded-md">
                        <img
                          src={EditIcon}
                          alt="edit-icon"
                          width={20}
                          height={20}
                          style={{
                            filter:
                              theme === "dark"
                                ? "brightness(0) invert(1)"
                                : "none",
                          }}
                        />
                      </button>

                      {/* Delete Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(strategy);
                        }}
                        disabled={deletingStrategy === strategy.id}
                        className={`p-1 hover:bg-n100 rounded-md ${deletingStrategy === strategy.id
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                          }`}>
                        {deletingStrategy === strategy.id ? (
                          <div className="w-5 h-5 border-2 border-n500 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <img
                            src={DeleteIcon}
                            alt="delete-icon"
                            width={20}
                            height={20}
                            style={{
                              filter:
                                theme === "dark"
                                  ? "brightness(0) invert(1)"
                                  : "none",
                            }}
                          />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 gap-y-2 p-3 overflow-y-auto max-h-[310px] no-scrollbar">
            {strategies[activeCategory]?.map((strategy) => (
              <div key={strategy.id} className="w-full">
                <div
                  className="flex flex-col items-center cursor-pointer p-3 relative transition duration-200"
                  onClick={() => handleStrategySelect(strategy.id)}>
                  {/* Chart Image */}
                  <div
                    className={`relative flex items-center justify-center w-[96px] h-[90px] mb-2 rounded-md
                                   ${selectedStrategy === strategy.id
                        ? "border-p300 ring-2 ring-p300"
                        : "border-n100 border-2  border-opacity-50"
                      }`}>
                    <img
                      src={strategy.image}
                      alt={strategy.name}
                      className="w-full h-full object-contain"
                    />

                    {/* Checkmark */}
                    {selectedStrategy === strategy.id && (
                      <div className="absolute top-0 right-0">
                        <img
                          src={TickIcon}
                          alt="Selected"
                          className="w-6 h-6"
                        />
                      </div>
                    )}

                    {/* New Badge */}
                    {strategy.isNew && (
                      <div className="absolute top-[-13px] left-1/2 transform -translate-x-1/2">
                        <img src={NewLabelIcon} alt="New" className="w-8 h-6" />
                      </div>
                    )}
                  </div>

                  {/* Strategy Name */}
                  <div
                    className={`text-center text-[10px] font-medium w-full
                                 ${selectedStrategy === strategy.id
                        ? "text-p300"
                        : "text-n500"
                      }`}>
                    {strategy.name || "Untitled"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={deleteConfirmation.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        strategyName={deleteConfirmation.strategy?.name}
        isDeleting={deletingStrategy === deleteConfirmation.strategy?.id}
      />
      {/* Edit Strategy Modal */}
      {editModalOpen && editingStrategy && (
        <SaveStrategyModal
          strategyData={{
            legs: editingStrategy.strategy.buy_sell_list?.map(
              (buySeell, index) => ({
                isBuy: buySeell === "Buy",
                isCALL:
                  editingStrategy.strategy.call_put_list[index] === "Call",
                isFuture:
                  editingStrategy.strategy.call_put_list[index] === "Fut",
                lotSize: editingStrategy.strategy.lots_list[index],
                atmIndex: editingStrategy.strategy.atmindex_list[index],
                expiries: editingStrategy.strategy.expiries[index],
              })
            ),
            selectedSymbol: selectedSymbol,
            allOptionsData: {}, // You might need to pass this from parent
          }}
          onClose={handleEditModalClose}
          onSave={handleEditModalSave}
          isEditMode={true}
          existingStrategyId={editingStrategy.id}
          existingStrategyName={editingStrategy.name}
        />
      )}
    </div>
  );
};

export default PrebuiltStrategies;
